/**
 * PermisBordureEauForm.jsx — Permis Travail en Bordure d'Eau (Lafarge v1 01/01/2024)
 * Sections: introduction, identification, check-list (émetteur + validateur),
 * dangers et mesures correctives, signataires.
 */
import { Waves } from 'lucide-react';
import FormationPicker from '@/components/FormationPicker';

const CHECKLIST = [
  ['cl_at_pp', '1. Les travaux ne peuvent être effectués sans Autorisation de Travail et plan de prévention'],
  ['cl_natation', '2. Personne autorisée formée et compétente (capacité de natation 50 m)'],
  ['cl_equipement', '3. Tout l\'équipement est en bon état'],
  ['cl_bouee', '4a. Bouée de sauvetage / ligne de vie en place et perche télescopique'],
  ['cl_scenario', '4b. Scénario de sauvetage défini'],
  ['cl_casque', '5a. Casque de sécurité avec jugulaire attachée'],
  ['cl_harnais', '5b. Harnais / longe de sécurité'],
  ['cl_vfi', '5c. Gilet de sauvetage individuel (VFI)'],
  ['cl_pas_seul', '5d. Interdiction de travailler seul'],
];

const DANGERS = [
  ['glissades', 'Glissades / Déplacements'],
  ['hauteur', 'Travailler en hauteur'],
  ['courants', 'Courants forts'],
  ['hypothermie', 'Hypothermie'],
  ['infection', 'Infection'],
  ['recuperation', 'Récupération difficile / Sauvetage'],
];

function YesNo({ value, onChange, testid }) {
  return (
    <div className="inline-flex gap-1" data-testid={testid}>
      {['oui', 'non'].map((v) => (
        <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
          className={`px-2 py-0.5 text-[11px] font-bold uppercase border ${
            value === v ? (v === 'oui' ? 'bg-green-600 text-white border-green-600' : 'bg-stone-700 text-white border-stone-700') : 'bg-white border-stone-300 text-stone-600'
          }`}>{v}</button>
      ))}
    </div>
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>{children}</div>
  );
}

export default function PermisBordureEauForm({ extra, setExtra, dateDebut, dateFin, titre, lieu, entreprise, compact = false, siteCode = '' }) {
  return (
    <div className="space-y-4 border-2 border-cyan-700 bg-white" data-testid="permis-bordure-eau-form">
      <div className="bg-cyan-700 text-white px-4 py-3 flex items-center gap-3">
        <Waves className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">PERMIS TRAVAIL EN BORDURE D'EAU</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">v1 (01/01/2024)</div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        <div className="text-[11px] text-stone-700 leading-snug bg-cyan-50 border border-cyan-200 p-3">
          Un permis est requis pour les activités quotidiennes qui ne sont pas exercées selon des procédures écrites
          gérant adéquatement les risques. Cela comprend : 1) Travailler sur la face d'un poste d'amarrage ou sous un poste
          d'amarrage ; 2) Travailler sur l'eau dans un bassin ou un canal ; 3) Utilisation d'un bateau, d'un ponton ou d'une
          barge comme plateforme de travail.
        </div>

        {/* Identification — En mode compact : on garde uniquement Heure début et Personne autorisée AT (les autres champs sont hérités) */}
        <div>
          <SectionTitle>Identification</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Heure de début</div>
              <input type="time" value={extra.heure_debut || ''} onChange={(e) => setExtra('heure_debut', e.target.value)}
                data-testid="be-heure-debut" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            {!compact && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Emplacement</div>
              <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{lieu || '—'}</div>
            </div>
            )}
            {!compact && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Émetteur du permis</div>
              <input type="text" value={extra.emetteur_nom || ''} onChange={(e) => setExtra('emetteur_nom', e.target.value)}
                data-testid="be-emetteur-nom" className="w-full px-2 py-1 border border-stone-300" placeholder="Nom" />
            </div>
            )}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Personne autorisée sur l'AT</div>
              <input type="text" value={extra.personne_autorisee || ''} onChange={(e) => setExtra('personne_autorisee', e.target.value)}
                data-testid="be-personne-autorisee" className="w-full px-2 py-1 border border-stone-300" placeholder="Nom Prénom" />
            </div>
          </div>
        </div>

        {/* Check-list */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Liste de contrôle</SectionTitle>
          <div className="border border-stone-200 border-t-0 bg-stone-50/50">
            <div className="grid grid-cols-[1fr,90px,90px] text-[10px] font-bold uppercase tracking-wider bg-stone-200 px-3 py-2">
              <div>Item</div>
              <div className="text-center">Émetteur (✓)</div>
              <div className="text-center">Validation (✓)</div>
            </div>
            {CHECKLIST.map(([key, label]) => (
              <div key={key} className="grid grid-cols-[1fr,90px,90px] items-center px-3 py-1.5 text-xs border-t border-stone-200">
                <span>{label}</span>
                <div className="flex justify-center">
                  <input type="checkbox" checked={!!extra[`${key}_emetteur`]}
                    onChange={(e) => setExtra(`${key}_emetteur`, e.target.checked)}
                    data-testid={`be-${key}-emetteur`} className="w-4 h-4 accent-[#DA291C]" />
                </div>
                <div className="flex justify-center">
                  <input type="checkbox" checked={!!extra[`${key}_validation`]}
                    onChange={(e) => setExtra(`${key}_validation`, e.target.checked)}
                    data-testid={`be-${key}-validation`} className="w-4 h-4 accent-cyan-700" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dangers & mesures correctives */}
        <div>
          <SectionTitle accent="bg-orange-700">Dangers & Mesures correctives</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs">
            {DANGERS.map(([k, label]) => (
              <div key={k} className="grid grid-cols-[200px,100px,1fr] items-center gap-2">
                <span>{label}</span>
                <YesNo value={extra[`danger_${k}`]} onChange={(v) => setExtra(`danger_${k}`, v)} testid={`be-danger-${k}`} />
                <input type="text" value={extra[`mesure_${k}`] || ''} onChange={(e) => setExtra(`mesure_${k}`, e.target.value)}
                  data-testid={`be-mesure-${k}`} className="px-2 py-1 border border-stone-300" placeholder="Mesure corrective" />
              </div>
            ))}
            <div className="grid grid-cols-[200px,100px,1fr] items-center gap-2">
              <input type="text" value={extra.danger_autre_desc || ''} onChange={(e) => setExtra('danger_autre_desc', e.target.value)}
                data-testid="be-danger-autre-desc" className="px-2 py-1 border border-stone-300" placeholder="Autre danger" />
              <YesNo value={extra.danger_autre} onChange={(v) => setExtra('danger_autre', v)} testid="be-danger-autre" />
              <input type="text" value={extra.mesure_autre || ''} onChange={(e) => setExtra('mesure_autre', e.target.value)}
                data-testid="be-mesure-autre" className="px-2 py-1 border border-stone-300" placeholder="Mesure corrective" />
            </div>
          </div>
        </div>

        {/* Signataires */}
        <div>
          <SectionTitle>Signataires</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-4 text-xs">
            <div className="space-y-2 border border-stone-200 p-3 bg-stone-50">
              <div className="font-bold text-[11px] uppercase tracking-wider text-[#DA291C]">Émetteur de permis</div>
              <input type="text" value={extra.emetteur_nom || ''} onChange={(e) => setExtra('emetteur_nom', e.target.value)}
                data-testid="be-emet-nom" className="w-full px-2 py-1 border border-stone-300" placeholder="Nom" />
              <input type="date" value={extra.emetteur_date || ''} onChange={(e) => setExtra('emetteur_date', e.target.value)}
                data-testid="be-emet-date" className="w-full px-2 py-1 border border-stone-300" />
              <input type="text" value={extra.emetteur_visa || ''} onChange={(e) => setExtra('emetteur_visa', e.target.value)}
                data-testid="be-emet-visa" className="w-full px-2 py-1 border border-stone-300" placeholder="Visa" />
            </div>
            <div className="space-y-2 border border-stone-200 p-3 bg-cyan-50/30">
              <div className="font-bold text-[11px] uppercase tracking-wider text-cyan-700">Validation Permis Bordure d'Eau</div>
              <FormationPicker
                module="bordure_eau"
                site={siteCode}
                value={extra.validateur_nom || ''}
                onChange={(name) => setExtra('validateur_nom', name)}
                testid="be-val-nom"
                placeholder="Signataire formé"
              />
              <input type="date" value={extra.validateur_date || ''} onChange={(e) => setExtra('validateur_date', e.target.value)}
                data-testid="be-val-date" className="w-full px-2 py-1 border border-stone-300" />
              <input type="text" value={extra.validateur_visa || ''} onChange={(e) => setExtra('validateur_visa', e.target.value)}
                data-testid="be-val-visa" className="w-full px-2 py-1 border border-stone-300" placeholder="Visa" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
